<script>
import imgCheckIcon from '@/components/countly/images/icons/check-icon.svg';
import FormCommons from '../form/commons';
import countlyVue from '../../compat/vue-core';
import i18n from '../../../../i18n';

export default countlyVue.components.BaseComponent.extend({
  inheritAttrs: false,
  mixins: [
    FormCommons.MultiStepForm,
    countlyVue.mixins.Modal,
  ],
  props: {
    isOpened: { type: Boolean, required: true },
    name: { type: String, required: true },
    title: { type: String, required: true },
    saveButtonLabel: { type: String, required: false, default: '' },
    cancelButtonLabel: { type: String, required: false, default: i18n.tc('common.cancel') },
    closeFn: { type: Function },
    hasCancelButton: { type: Boolean, required: false, default: true },
    toggleTransition: {
      type: String,
      default: 'stdt-slide-right',
    },
    hideStepNames: { type: Boolean, default: false },
    size: {
      type: Number,
      default: 6,
      validator(value) {
        return value >= 1 && value <= 12;
      },
    },
  },
  data() {
    return {
      isToggleable: true,
      sidecarContents: [],
      imgCheckIcon,
    };
  },
  computed: {
    hasSidecars() {
      return this.sidecarContents.length > 0;
    },
    rootClasses() {
      const classes = {
        'is-mounted': this.isMounted,
        'is-open': this.isOpened,
        'has-sidecars': this.hasSidecars,
      };
      classes[`cly-vue-drawer--${this.currentScreenMode}-screen`] = true;
      if (this.currentScreenMode === 'half') {
        classes[`cly-vue-drawer--half-screen-${this.size}`] = true;
      }
      return classes;
    },
  },
  watch: {
    isOpened(newState) {
      if (!newState) {
        this.reset();
      } else {
        this.$emit('open');
      }
      this.setModalState(newState);
    },
  },
  mounted() {
    this.sidecarContents = this.$children.filter((child) => child.isContent && child.role === 'sidecar');
  },
  methods: {
    doClose() {
      this.$emit('close', this.name);
      if (this.closeFn) {
        this.closeFn();
      }
    },
    escKeyEvent() {
      this.doClose();
    },
    onViewEntered() {
      this.$refs.rootEl.focus();
    },
    onNextPressed() {
      this.nextStep();
      // this.$emit('next', this.currentStepIndex);
    },
  },
});
</script>
<template>
  <transition
    :name="toggleTransition"
    @enter="onViewEntered"
  >
    <div
      v-show="isOpened"
      ref="rootEl"
      class="cly-vue-drawer"
      tabindex="0"
      :class="rootClasses"
      @keydown.esc="escKeyEvent"
    >
      <div
        v-show="hasSidecars"
        class="cly-vue-drawer__sidecars-view"
      >
        <slot
          name="sidecars"
          v-bind="passedScope"
        />
      </div>
      <div class="cly-vue-drawer__steps-view relative">
        <div class="cly-vue-drawer__steps-wrapper">
          <div
            v-if="currentScreenMode === 'full'"
            class="bu-container bu-pt-3 bu-is-fluid bu-p-0"
          >
            <div class="bu-columns bu-is-gapless">
              <div class="bu-column bu-is-12 bu-is-flex bu-is-justify-content-flex-end">
                <span
                  class="cly-vue-drawer__close-button"
                  @click="doClose"
                >
                  <i class="ion-ios-close-empty" />
                </span>
              </div>
            </div>
          </div>
          <div
            class="cly-vue-drawer__header border-b border-warm-gray-30"
            :class="{ 'is-full-screen': currentScreenMode === 'full'}"
          >
            <!-- <h3 class="cly-vue-drawer__title-header">
              {{ title }}
            </h3> -->
            <div class="cly-vue-drawer__title">
              <div v-if="currentScreenMode !== 'full'">
                <div class="cly-vue-drawer__title-container bu-is-flex bu-is-justify-content-space-between">
                  <h3 class="cly-vue-drawer__title-header">
                    {{ title }}
                  </h3>
                  <span
                    class="cly-vue-drawer__close-button bu-p-1 bu-mr-5"
                    @click="doClose"
                  >
                    <i class="ion-ios-close-empty" />
                  </span>
                </div>
              </div>
              <div
                v-if="isMultiStep"
                class="bu-columns bu-is-gapless bu-is-mobile cly-vue-drawer__subtitle"
                :class="currentScreenMode !== 'full' && '!mx-8'"
              >
                <div class="bu-column bu-is-12 bu-is-flex bu-is-align-items-center bu-is-justify-content-left">
                  <div
                    v-if="!hideStepNames"
                    class="cly-vue-drawer__steps-header bu-is-flex-direction-column"
                  >
                    <div
                      v-if="currentScreenMode === 'full' && !hideStepNames"
                      class="absolute -top-8 left-0"
                    >
                      <h3>{{ title }}</h3>
                    </div>
                    <div class="bu-is-justify-content-space-between bu-is-flex bu-is-align-items-center w-full mt-3">
                      <template v-for="(currentContent, i) in stepContents">
                        <div
                          :key="'label_' + i"
                          class="cly-vue-drawer__step-label"
                          :class="{'is-locked': i > lastValidIndex, 'is-active': i === currentStepIndex, 'is-passed': i < currentStepIndex}"
                          @click="setStepSafe(i, 'header-click')"
                        >
                          <div class="bu-is-flex">
                            <div class="cly-vue-drawer__step-sign">
                              <span
                                class="index text-small"
                                :class="{'color-white': i === currentStepIndex}"
                              >{{ i + 1 }}</span>
                              <span class="done-icon text-small color-white bu-pt-0">
                                <img :src="imgCheckIcon">
                              </span>
                            </div>
                            <div class="cly-vue-drawer__step-title text-small font-weight-bold color-cool-gray-40">
                              {{ currentContent.name }}
                            </div>
                          </div>
                        </div>
                        <div
                          v-if="i < stepContents.length - 1"
                          :key="'sep_' + i"
                          class="cly-vue-drawer__step-separator"
                        />
                      </template>
                    </div>
                  </div>
                  <div
                    v-else
                    class="-ml-20 font-medium text-2xl"
                  >
                    {{ title }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            v-scroll-shadow
            class="cly-vue-drawer__steps-container"
            :class="{'is-multi-step':isMultiStep}"
          >
            <div
              v-if="currentScreenMode === 'full' && hideStepNames"
              class="bu-columns bu-is-gapless bu-is-mobile cly-vue-drawer__body-container"
            >
              <div class="bu-column bu-is-12">
                <h3>{{ title }}</h3>
              </div>
            </div>
            <div
              class="bu-columns bu-is-gapless bu-is-mobile cly-vue-drawer__body-container !m-0"
              :class="{ 'bu-pb-5 bu-pt-4 bu-mb-2 bu-mt-1': currentScreenMode !== 'full' }"
            >
              <div class="bu-column bu-is-12">
                <slot
                  name="default"
                  v-bind="passedScope"
                />
              </div>
            </div>
          </div>
          <div
            v-if="isMultiStep || hasCancelButton || saveButtonLabel"
            class="cly-vue-drawer__footer !border-t border-warm-gray-30 flex justify-between"
          >
            <!-- <div class="cly-vue-drawer__controls-left-pc">
              <slot
                name="controls-left"
                v-bind="passedScope"
              />
            </div> -->
            <div class="w-full">
              <slot
                name="footer-left"
                v-bind="passedScope"
              />
            </div>
            <div
              v-if="isMultiStep"
              class="cly-vue-drawer__buttons is-multi-step is-single-step bu-is-justify-content-flex-end bu-is-flex items-center"
            >
              <el-button
                v-if="currentStepIndex === 0 && hasCancelButton"
                class="h-fit"
                type="secondary"
                size="small"
                :disabled="isSubmitPending || isNextPending"
                @click="doClose"
              >
                {{ cancelButtonLabel }}
              </el-button>
              <el-button
                v-if="currentStepIndex > 0"
                class="h-fit"
                type="secondary"
                size="small"
                :disabled="isSubmitPending || isNextPending"
                @click="prevStep"
              >
                {{ $t('common.drawer.previous-step') }}
              </el-button>
              <el-button
                v-if="!isLastStep"
                class="h-fit"
                :data-test="`${name}-drawer-next-btn`"
                type="success"
                size="small"
                :class="{'is-disabled':!isCurrentStepValid}"
                :disabled="isSubmitPending || isNextPending"
                @click="onNextPressed"
              >
                {{ $t('common.drawer.next-step') }}
              </el-button>
              <el-button
                v-if="isLastStep"
                class="h-fit"
                :data-test="`${name}-drawer-submit-btn`"
                type="success"
                :loading="isSubmitPending || isNextPending"
                size="small"
                :class="{'is-disabled':!isSubmissionAllowed}"
                :disabled="isSubmitPending || isNextPending"
                @click="submit"
              >
                {{ saveButtonLabel }}
              </el-button>
            </div>
            <div
              v-if="!isMultiStep"
              class="cly-vue-drawer__buttons is-single-step is-single-step bu-is-justify-content-flex-end bu-is-flex"
            >
              <el-button
                v-if="hasCancelButton"
                class="h-fit"
                type="secondary"
                size="small"
                :disabled="isSubmitPending || isNextPending"
                @click="doClose"
              >
                {{ cancelButtonLabel }}
              </el-button>
              <el-button
                v-if="saveButtonLabel"
                class="h-fit"
                type="success"
                :loading="isSubmitPending || isNextPending"
                size="small"
                :class="{'is-disabled':!isSubmissionAllowed}"
                :disabled="isSubmitPending || isNextPending"
                @click="submit"
              >
                {{ saveButtonLabel }}
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>
